import axios from 'axios'
import type {
  TranslatedMaterial,
  TranslatedMaterialData,
  Language,
  Memory,
  Material,
  SearchResult,
  Profile,
  ProfileSettings,
  SavedProfile,
  StudyCardsResponse,
  StudyMode,
  CardsDeckResponse,
  CardsModuleProgress,
  CardsDashboardResponse,
} from '@shared/types'
import type {TtsLanguageSettings, TtsPreviewRequest, TtsSettingsResponse} from '@shared/tts_types'
import type {
  ModelRole,
  ModelRoleConfig,
  ModelSettingsResponse,
  ReasoningEffort,
  OpenRouterModel,
} from '@shared/llm_types'
import type {
  NextCardDetail,
  NextCardSummary,
  NextCardImageFilter,
  NextCatalogMetadata,
  NextConceptSummary,
  NextEntityDetail,
  NextEntityGraph,
  NextEntitySummary,
  NextPage,
  NextSectionId,
} from '@shared/next'

export const api = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true,
})

export function nextMediaUrl(imagePath: string, imageHash?: string | null, width?: number): string {
  const relativePath = imagePath.replace(/^media\//u, '')
  const base = `${import.meta.env.VITE_BASE_URL || ''}/next-media/${relativePath}`
  const params = new URLSearchParams()
  if (imageHash) params.append('hash', imageHash)
  if (width) params.append('w', String(width))
  const query = params.toString()
  return query ? `${base}?${query}` : base
}

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      error.config?.url !== '/api/auth/password' &&
      window.location.pathname !== '/auth'
    ) {
      // Redirect to auth page if unauthorized
      console.log('Unauthorized - redirecting to auth')
      window.location.href = '/auth'
    }
    return Promise.reject(error)
  }
)

// Types for API responses
export interface TranslationStatus {
  translate_id: number
  material_id: number
  status: 'check' | 'ok' | 'sample'
}

export interface GeneratedTranslation {
  title: string
  translations: Array<{
    default: string
    commented?: string
    error: string | null
  }>
}

export interface MaterialIndexResponse {
  levels: Array<{level: number; slides_count: number}>
  units: Array<{level: number; unit: number}>
  lessons: Array<{level: number; unit: number; lesson: number; slides_count: number}>
}

export interface MaterialSlide {
  level: number
  unit: number
  lesson: number
  slide: number
  title: string
  material_id: number
  data: Array<{text?: string; image?: string; description?: {short: string; full: string}}>
}

export interface TotalProgressItem {
  level: number
  unit: number
  lesson: number
  completed: number
}

export interface ProgressItem {
  slide: number
  result: number | null
}

export interface AuthUser {
  email: string
  role: 'user' | 'manager' | 'admin'
  settings: import('@shared/types').UserSettings | null
}

export interface UserSummary extends AuthUser {
  user_id: number
  auth_methods: Array<'google' | 'password'>
}

export interface EmailDigestPreview {
  subject: string
  html: string
}

export const authApi = {
  signInWithPassword: async (email: string, password: string): Promise<AuthUser> => {
    const response = await api.post('/api/auth/password', {email, password})
    return response.data
  },
}

export const clientApi = {
  materials: {
    // Get material index for a target language
    getMaterialIndex: async (targetLanguage: string): Promise<MaterialIndexResponse> => {
      const response = await api.get(`/api/materials/index/${targetLanguage}`)
      return response.data
    },

    // Get material by ID
    getMaterialById: async (materialId: number): Promise<Material> => {
      const response = await api.get(`/api/materials/${materialId}`)
      return response.data
    },

    // Get slides for a specific level, unit, lesson
    getSlides: async (
      targetLanguage: string,
      nativeLanguage: string,
      level: number,
      unit: number,
      lesson: number
    ): Promise<MaterialSlide[]> => {
      const response = await api.get(`/api/materials/${targetLanguage}/${nativeLanguage}/${level}/${unit}/${lesson}`)
      return response.data
    },

    // Get slide by lesson and slide number
    getSlideByLesson: async (
      targetLanguage: string,
      level: number,
      unit: number,
      lesson: number,
      slide: number
    ): Promise<MaterialSlide> => {
      const response = await api.get(`/api/materials/by-lesson/${targetLanguage}/${level}/${unit}/${lesson}/${slide}`)
      return response.data
    },

    // Get total progress for a mode
    getTotalProgress: async (mode: string, profileId?: number): Promise<TotalProgressItem[]> => {
      const queryParams = profileId ? `?profile_id=${profileId}` : ''
      const response = await api.get(`/api/total-progress/${mode}${queryParams}`)
      return response.data
    },

    // Get progress for a specific level, unit, lesson
    getProgress: async (
      mode: string,
      level: number,
      unit: number,
      lesson: number,
      profileId?: number
    ): Promise<ProgressItem[]> => {
      const queryParams = profileId ? `?profile_id=${profileId}` : ''
      const response = await api.get(`/api/progress/${mode}/${level}/${unit}/${lesson}${queryParams}`)
      return response.data
    },

    getStudyCards: async (materialId: number, lang: string): Promise<StudyCardsResponse> => {
      const response = await api.get(`/api/materials/${materialId}/cards`, {params: {lang}})
      return response.data
    },
  },

  cards: {
    getDeck: async (params: {
      profile_id: number
      targetLanguage: string
      nativeLanguage: string
      mode: StudyMode
      level?: number
      unit?: number | null
      lesson?: number
      slide?: number
      shuffle?: boolean
      limit?: number
      source?: string
      preview?: boolean
    }): Promise<CardsDeckResponse> => {
      const query = new URLSearchParams()
      query.append('profile_id', String(params.profile_id))
      query.append('targetLanguage', params.targetLanguage)
      query.append('nativeLanguage', params.nativeLanguage)
      query.append('mode', params.mode)
      if (params.level !== undefined) query.append('level', String(params.level))
      if (params.unit !== undefined && params.unit !== null) query.append('unit', String(params.unit))
      if (params.lesson !== undefined && params.lesson !== null) query.append('lesson', String(params.lesson))
      if (params.slide !== undefined && params.slide !== null) query.append('slide', String(params.slide))
      if (params.shuffle) query.append('shuffle', 'true')
      if (params.limit !== undefined) query.append('limit', String(params.limit))
      if (params.source) query.append('source', params.source)
      if (params.preview) query.append('preview', 'true')
      const response = await api.get(`/api/cards/deck?${query.toString()}`)
      return response.data
    },
    grade: async (
      profile_id: number,
      mode: StudyMode,
      card_key: string,
      grade: number,
      nativeLanguage?: string,
      targetLanguage?: string
    ) => {
      const response = await api.post('/api/cards/grade', {
        profile_id,
        mode,
        card_key,
        grade,
        nativeLanguage,
        targetLanguage,
      })
      return response.data
    },
    getModuleProgress: async (
      profile_id: number,
      targetLanguage: string,
      nativeLanguage: string
    ): Promise<{modules: CardsModuleProgress[]}> => {
      const query = new URLSearchParams({
        profile_id: String(profile_id),
        targetLanguage,
        nativeLanguage,
      })
      const response = await api.get(`/api/cards/module-progress?${query.toString()}`)
      return response.data
    },
    getDue: async (profile_id: number, mode: StudyMode) => {
      const response = await api.get(`/api/cards/due?profile_id=${profile_id}&mode=${mode}`)
      return response.data
    },
    getDashboard: async (profile_id: number): Promise<CardsDashboardResponse> => {
      const response = await api.get(`/api/cards/dashboard?profile_id=${profile_id}`)
      return response.data
    },
  },
}

export const cpApi = {
  next: {
    metadata: async (): Promise<NextCatalogMetadata> => (await api.get('/api/cp/next/metadata')).data,
    list: async (
      section: NextSectionId,
      params: {
        query?: string
        kind?: string
        cefr?: string
        concept?: string
        imageFilter?: NextCardImageFilter
        languages?: string[]
        page: number
        pageSize: number
      }
    ): Promise<NextPage<NextConceptSummary | NextEntitySummary | NextCardSummary>> =>
      (
        await api.get(`/api/cp/next/${section}`, {
          params: {
            q: params.query,
            kind: params.kind,
            cefr: params.cefr,
            concept: params.concept,
            image_filter: params.imageFilter,
            languages: params.languages?.join(','),
            page: params.page,
            page_size: params.pageSize,
          },
        })
      ).data,
    card: async (cardId: string, languages: string[]): Promise<NextCardDetail> =>
      (await api.get(`/api/cp/next/cards/${encodeURIComponent(cardId)}`, {params: {languages: languages.join(',')}}))
        .data,
    entity: async (entityId: string): Promise<NextEntityDetail> =>
      (await api.get(`/api/cp/next/entities/${encodeURIComponent(entityId)}`)).data,
    entityGraph: async (entityId: string, depth = 1): Promise<NextEntityGraph> =>
      (
        await api.get(`/api/cp/next/entities/${encodeURIComponent(entityId)}/graph`, {
          params: {depth},
        })
      ).data,
    uploadCardImage: async (
      cardId: string,
      index: number,
      file: File | Blob
    ): Promise<{image: string; imageHash: string}> =>
      (
        await api.post(`/api/cp/next/cards/${encodeURIComponent(cardId)}/images/${index}/upload`, file, {
          headers: {'Content-Type': (file as File).type || 'application/octet-stream'},
        })
      ).data,
    uploadCardImageUrl: async (
      cardId: string,
      index: number,
      imageUrl: string
    ): Promise<{image: string; imageHash: string}> =>
      (await api.post(`/api/cp/next/cards/${encodeURIComponent(cardId)}/images/${index}/upload`, {imageUrl}))
        .data,
    uploadEntityImage: async (
      entityId: string,
      slot: string,
      file: File | Blob
    ): Promise<{image: string; imageHash: string}> =>
      (
        await api.post(
          `/api/cp/next/entities/${encodeURIComponent(entityId)}/images/${encodeURIComponent(slot)}/upload`,
          file,
          {
            headers: {'Content-Type': (file as File).type || 'application/octet-stream'},
          }
        )
      ).data,
    uploadEntityImageUrl: async (
      entityId: string,
      slot: string,
      imageUrl: string
    ): Promise<{image: string; imageHash: string}> =>
      (
        await api.post(
          `/api/cp/next/entities/${encodeURIComponent(entityId)}/images/${encodeURIComponent(slot)}/upload`,
          {imageUrl}
        )
      ).data,
  },
  emailDigest: {
    preview: async (profileId: number): Promise<EmailDigestPreview> =>
      (await api.post('/api/cp/email-digest/preview', {profile_id: profileId})).data,
    sendPreview: async (profileId: number): Promise<{subject: string}> =>
      (await api.post('/api/cp/email-digest/preview/send', {profile_id: profileId})).data,
  },
  ttsSettings: {
    get: async (): Promise<TtsSettingsResponse> => (await api.get('/api/cp/tts-settings')).data,
    refresh: async (): Promise<TtsSettingsResponse> => (await api.post('/api/cp/tts-settings/catalog/refresh')).data,
    save: async (language: string, value: TtsLanguageSettings): Promise<TtsLanguageSettings> =>
      (await api.put(`/api/cp/tts-settings/languages/${language}`, value)).data,
    preview: async (value: TtsPreviewRequest): Promise<Blob> =>
      (await api.post('/api/cp/tts-settings/preview', value, {responseType: 'blob'})).data,
  },
  modelSettings: {
    get: async (): Promise<ModelSettingsResponse> => (await api.get('/api/cp/model-settings')).data,
    refresh: async (): Promise<ModelSettingsResponse> => (await api.post('/api/cp/model-settings/refresh')).data,
    saveRole: async (
      role: ModelRole,
      value: {model: string | null; reasoning: ReasoningEffort | null}
    ): Promise<ModelRoleConfig> => (await api.put(`/api/cp/model-settings/roles/${role}`, value)).data,
  },
  users: {
    list: async (): Promise<UserSummary[]> => (await api.get('/api/cp/users')).data,
    create: async (email: string, password: string): Promise<UserSummary> =>
      (await api.post('/api/cp/users', {email, password})).data,
    update: async (userId: number, changes: {email?: string; password?: string}): Promise<UserSummary> =>
      (await api.patch(`/api/cp/users/${userId}`, changes)).data,
  },
  translation: {
    // Get translations with optional filters
    getTranslations: async <T = TranslatedMaterial | TranslationStatus>(params: {
      material_id?: number
      lang: string
      fields?: string
      target_lang?: string
    }): Promise<T[]> => {
      const queryParams = new URLSearchParams()
      if (params.material_id) queryParams.append('material_id', params.material_id.toString())
      if (params.lang) queryParams.append('lang', params.lang)
      if (params.fields) queryParams.append('fields', params.fields)
      if (params.target_lang) queryParams.append('target_lang', params.target_lang)

      const response = await api.get(`/api/cp/translations?${queryParams.toString()}`)
      return response.data as T[]
    },

    // Generate translation
    generate: async (materialId: number, lang: string): Promise<GeneratedTranslation> => {
      const response = await api.post(`/api/cp/translation/generate/${materialId}`, {lang})
      return response.data
    },

    // Generate commented translation
    generateCommented: async (
      materialId: number,
      params: {
        lang: string
        translations: Array<{default: string}>
        index?: number
      }
    ): Promise<GeneratedTranslation> => {
      const response = await api.post(`/api/cp/translation/generate-commented/${materialId}`, params)
      return response.data
    },

    // Generate default translation
    generateDefault: async (materialId: number, lang: string): Promise<GeneratedTranslation> => {
      const response = await api.post(`/api/cp/translation/generate-default/${materialId}`, {lang})
      return response.data
    },

    // Create a new translation
    create: async (params: {
      material_id: number
      lang: string
      title: string
      data: TranslatedMaterialData
      status: 'check' | 'ok' | 'sample'
    }): Promise<TranslatedMaterial> => {
      const response = await api.post('/api/cp/translation', params)
      return response.data
    },

    // Update an existing translation
    update: async (
      translateId: number,
      params: {
        title?: string
        data?: TranslatedMaterialData
        status?: 'check' | 'ok' | 'sample'
      }
    ): Promise<TranslatedMaterial> => {
      const response = await api.put(`/api/cp/translation/${translateId}`, params)
      return response.data
    },
  },

  search: {
    // Search materials and translations
    search: async (params: {
      text: string
      target_lang: string
      native_lang: string
      scope?: 'material' | 'translate' | 'both'
      skip?: number
      limit?: number
    }): Promise<SearchResult> => {
      const queryParams = new URLSearchParams()
      queryParams.append('text', params.text)
      queryParams.append('target_lang', params.target_lang)
      queryParams.append('native_lang', params.native_lang)
      if (params.scope) queryParams.append('scope', params.scope)
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString())

      const response = await api.get(`/api/cp/search?${queryParams.toString()}`)
      return response.data
    },
  },
}

export const profileApi = {
  list: async (): Promise<SavedProfile[]> => (await api.get('/api/profiles')).data,
  create: async (settings: ProfileSettings): Promise<{profile_id: number}> =>
    (await api.post('/api/profile', {settings})).data,
  update: async (profileId: number, profile: Partial<Profile>): Promise<void> => {
    await api.put(`/api/profile/${profileId}`, profile)
  },
  remove: async (profileId: number): Promise<void> => {
    await api.delete(`/api/profile/${profileId}`)
  },
  memory: async (profileId: number): Promise<Memory[]> => (await api.get(`/api/profile/${profileId}/memory`)).data,
  deleteMemory: async (profileId: number, memoryId: number): Promise<void> => {
    await api.delete(`/api/profile/${profileId}/memory/${memoryId}`)
  },
}
